import React, { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, query, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Container,
  Grid2 as Grid,
  Fade,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../AuthContext';

// Modern color palette (consistent with Topics.jsx)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
    '& svg': {
      transform: 'translateX(6px)',
    },
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1, 2),
  color: '#e74c3c',
  borderColor: '#e74c3c',
  '&:hover': {
    backgroundColor: '#e74c3c',
    color: 'white',
    borderColor: '#e74c3c',
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 },
  maxWidth: '100%',
  backgroundColor: 'white',
  borderRadius: { xs: '8px', sm: '12px' },
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  padding: { xs: theme.spacing(2), sm: theme.spacing(4) },
  maxHeight: '90vh',
  overflowY: 'auto',
}));

const PastPapers = () => {
  const [pastPapers, setPastPapers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPastPaper, setSelectedPastPaper] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Access status options
  const accessStatusOptions = [
    { value: 'trial', label: 'Trial' },
    { value: 'premium', label: 'Premium' },
  ];

  // Check if user is admin
  useEffect(() => {
    if (!user) {
      navigate('/sign-in');
    } else {
      const checkAdmin = async () => {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().role !== 'admin') {
          navigate('/dashboard');
        }
      };
      checkAdmin().catch((error) => {
        console.error('Error checking admin status:', error);
        navigate('/sign-in');
      });
    }
  }, [user, navigate]);

  // Fetch past papers
  useEffect(() => {
    const fetchPastPapers = async () => {
      setIsLoading(true);
      try {
        const pastPapersCollection = collection(db, 'pastPapers');
        const pastPapersSnapshot = await getDocs(query(pastPapersCollection));
        const pastPapersData = pastPapersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          url: doc.data().url || 'N/A',
          solutionsUrl: doc.data().solutionsUrl || 'N/A',
          accessStatus: doc.data().accessStatus || 'trial',
          category: doc.data().category || '',
        }));
        setPastPapers(pastPapersData);
      } catch (error) {
        console.error('Error fetching past papers:', { message: error.message, code: error.code });
        setSnackbarMessage('Error fetching past papers');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPastPapers();
  }, []);

  // Filter past papers
  const filteredPastPapers = pastPapers
    .filter((paper) => {
      const matchesText =
        paper.title.toLowerCase().includes(searchText.toLowerCase()) ||
        paper.description.toLowerCase().includes(searchText.toLowerCase());
      return matchesText;
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  // Handle edit click
  const handleEditClick = (paper) => {
    setSelectedPastPaper(paper);
    setOpenModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPastPaper(null);
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // Validate URL format
  const isValidUrl = (url) => {
    if (!url || url === 'N/A') return true; // Allow empty or N/A URLs
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Handle save past paper
  const handleSavePastPaper = async () => {
    if (!selectedPastPaper) return;

    const currentYear = new Date().getFullYear();
    if (
      !selectedPastPaper.title.trim() ||
      !selectedPastPaper.description.trim() ||
      !selectedPastPaper.year ||
      isNaN(selectedPastPaper.year) ||
      selectedPastPaper.year < 1900 ||
      selectedPastPaper.year > currentYear ||
      !selectedPastPaper.subject.trim() ||
      !selectedPastPaper.category.trim() ||
      !selectedPastPaper.schoolLevel.trim() ||
      !selectedPastPaper.url.trim() ||
      !isValidUrl(selectedPastPaper.url) ||
      (selectedPastPaper.solutionsUrl && selectedPastPaper.solutionsUrl !== 'N/A' && !isValidUrl(selectedPastPaper.solutionsUrl)) ||
      !selectedPastPaper.accessStatus
    ) {
      setSnackbarMessage('Please fill all required fields with valid data.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const paperRef = doc(db, 'pastPapers', selectedPastPaper.id);
      await updateDoc(paperRef, {
        title: selectedPastPaper.title.trim(),
        description: selectedPastPaper.description.trim(),
        year: parseInt(selectedPastPaper.year, 10),
        subject: selectedPastPaper.subject.trim(),
        category: selectedPastPaper.category.trim(),
        schoolLevel: selectedPastPaper.schoolLevel.trim(),
        url: selectedPastPaper.url.trim(),
        solutionsUrl: selectedPastPaper.solutionsUrl ? selectedPastPaper.solutionsUrl.trim() : '',
        accessStatus: selectedPastPaper.accessStatus,
      });
      setPastPapers(
        pastPapers.map((paper) =>
          paper.id === selectedPastPaper.id ? { ...selectedPastPaper, solutionsUrl: selectedPastPaper.solutionsUrl || 'N/A' } : paper
        )
      );
      handleCloseModal();
      setSnackbarMessage('Past paper updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating past paper:', { message: error.message, code: error.code });
      setSnackbarMessage('Failed to update past paper.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle delete past paper
  const handleDeletePastPaper = async (paperId) => {
    if (window.confirm('Are you sure you want to delete this past paper? This action cannot be undone.')) {
      try {
        const paperRef = doc(db, 'pastPapers', paperId);
        await deleteDoc(paperRef);
        setPastPapers(pastPapers.filter((paper) => paper.id !== paperId));
        setSnackbarMessage('Past paper deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('Error deleting past paper:', { message: error.message, code: error.code });
        setSnackbarMessage('Failed to delete past paper.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // Handle input change in edit modal
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedPastPaper((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // DataGrid columns
  const columns = [
    { field: 'title', headerName: 'Title', width: 200 },
    { field: 'description', headerName: 'Description', width: 200 },
    { field: 'year', headerName: 'Year', width: 100 },
    { field: 'subject', headerName: 'Subject', width: 150 },
    { field: 'category', headerName: 'Category', width: 150 },
    { field: 'schoolLevel', headerName: 'School Level', width: 150 },
    {
      field: 'accessStatus',
      headerName: 'Access Status',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ color: params.value === 'premium' ? colors.accent : colors.primary, fontWeight: '600' }}>
          {params.value.charAt(0).toUpperCase() + params.value.slice(1)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 400,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEditClick(params.row)}
          >
            Edit
          </AnimatedButton>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              if (params.row.url && params.row.url !== 'N/A') {
                window.open(params.row.url, '_blank', 'noopener,noreferrer');
              } else {
                setSnackbarMessage('No download URL available for this past paper.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              }
            }}
          >
            View Paper
          </AnimatedButton>
          <AnimatedButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              if (params.row.solutionsUrl && params.row.solutionsUrl !== 'N/A') {
                window.open(params.row.solutionsUrl, '_blank', 'noopener,noreferrer');
              } else {
                setSnackbarMessage('No solutions URL available for this past paper.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
              }
            }}
          >
            View Solutions
          </AnimatedButton>
          <DeleteButton
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleDeletePastPaper(params.row.id)}
          >
            Delete
          </DeleteButton>
        </Box>
      ),
    },
  ];

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Box
            sx={{
              mb: 5,
              p: 4,
              borderRadius: '16px',
              background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
              color: 'white',
              boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
              <LibraryBooksIcon sx={{ fontSize: 35, mr: 1 }} />
              Past Papers
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
              Manage and edit past papers in the Maths Gem Seychelles portal.
            </Typography>
          </Box>
        </Fade>

        {/* Filters and Add Button */}
        <ModernCard>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: colors.secondary, fontWeight: '600' }}>
                Past Papers List
              </Typography>
              <AnimatedButton
                variant="contained"
                color="primary"
                onClick={() => navigate('/addpastpapers')}
                startIcon={<AddIcon />}
              >
                Add New Past Paper
              </AnimatedButton>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <TextField
                  fullWidth
                  label="Search Past Papers"
                  variant="outlined"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by title or description"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.primary },
                      '&:hover fieldset': { borderColor: colors.accent },
                      '&.Mui-focused fieldset': { borderColor: colors.accent },
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.secondary,
                      '&.Mui-focused': { color: colors.accent },
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* DataGrid */}
            <Box sx={{ mt: 3 }}>
              <DataGrid
                rows={filteredPastPapers}
                columns={columns}
                pageSize={10}
                checkboxSelection={false}
                loading={isLoading}
                initialState={{
                  sorting: { sortModel: [{ field: 'title', sort: 'asc' }] },
                }}
                sx={{
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { color: colors.secondary },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.background,
                    color: colors.secondary,
                    fontWeight: '600',
                  },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4f8' },
                }}
              />
            </Box>
          </Box>
        </ModernCard>

        {/* Edit Modal */}
        <Modal open={openModal} onClose={handleCloseModal}>
          <ModalBox sx={{ padding: 3, borderRadius: 5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: '700', color: colors.secondary, mb: 3 }}
            >
              Edit Past Paper
            </Typography>
            {selectedPastPaper && (
              <Box component="form">
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={selectedPastPaper.title}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={selectedPastPaper.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Year"
                  name="year"
                  value={selectedPastPaper.year}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={selectedPastPaper.subject}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Past Paper Category"
                  name="category"
                  value={selectedPastPaper.category}
                  onChange={handleInputChange}
                  placeholder="e.g. Mock Exams, Final Exams, End of Term"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="School Level"
                  name="schoolLevel"
                  value={selectedPastPaper.schoolLevel}
                  onChange={handleInputChange}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Download URL"
                  name="url"
                  value={selectedPastPaper.url}
                  onChange={handleInputChange}
                  type="url"
                  placeholder="https://example.com/pastpaper.pdf"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Solutions URL"
                  name="solutionsUrl"
                  value={selectedPastPaper.solutionsUrl}
                  onChange={handleInputChange}
                  type="url"
                  placeholder="https://example.com/solutions.pdf"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: colors.secondary }}>Access Status</InputLabel>
                  <Select
                    name="accessStatus"
                    value={selectedPastPaper.accessStatus}
                    onChange={handleInputChange}
                    label="Access Status"
                    sx={{
                      borderRadius: '10px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                    }}
                  >
                    {accessStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <AnimatedButton
                    variant="contained"
                    color="primary"
                    onClick={handleSavePastPaper}
                  >
                    Save
                  </AnimatedButton>
                  <AnimatedButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </AnimatedButton>
                </Box>
              </Box>
            )}
          </ModalBox>
        </Modal>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? colors.primary : '#e74c3c', color: 'white' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default PastPapers;